<template>
	<div class="portal-documents">
		<header class="header-bar">
			<h2 class="section-title">Supplier documents</h2>
		</header>

		<div class="controls-row">
			<div ref="createMenuRef" class="create-wrapper" @keydown.esc.stop="closeCreateMenu">
				<button
					type="button"
					class="new-doc-btn action-btn action-btn--primary"
					aria-haspopup="menu"
					:aria-expanded="showCreateMenu"
					aria-controls="supplier-new-document-menu"
					aria-label="Create a supplier document"
					@click.stop="toggleCreateMenu"
					:disabled="isCreatingDocument"
				>
					<AppointmentIcon name="plus" class="action-btn-icon" />
					<span>New document</span>
				</button>
				<div
					v-if="showCreateMenu"
					id="supplier-new-document-menu"
					class="create-menu"
					role="menu"
				>
					<button
						type="button"
						class="create-menu-option"
						role="menuitem"
						@click="openNewDocumentModal"
					>
						<span class="create-menu-option-title">Upload file</span>
					</button>
					<button
						type="button"
						class="create-menu-option"
						role="menuitem"
						@click="createAssignmentConfirmation"
					>
						<span class="create-menu-option-title">Assignment confirmation</span>
					</button>
				</div>
			</div>
			<section class="filters-bar" aria-label="Document filters">
				<div class="filters-bar-start">
					<AppointmentIcon name="filter" class="filter-bar-icon" />
				</div>
				<div class="search-wrapper">
					<AppointmentIcon name="search" :size="16" class="search-icon" />
					<input
						v-model="search"
						type="text"
						class="input search-input"
						data-refresh-safe
						placeholder="Search..."
						aria-label="Search supplier documents"
					/>
				</div>
				<div class="category-filter-group" role="group" aria-label="Filter by category">
					<button
						v-for="category in categoryFilters"
						:key="category.value"
						type="button"
						class="category-filter-btn"
						:class="{ active: activeCategory === category.value }"
						:aria-pressed="activeCategory === category.value"
						@click="activeCategory = category.value"
					>
						{{ category.label }}
					</button>
				</div>
			</section>
		</div>

		<div v-if="isLoading" class="loading-container">
			<div class="loading-spinner"></div>
			<p>Loading documents...</p>
		</div>

		<div v-else-if="error" class="error-container">
			<p class="error-message">{{ error }}</p>
		</div>

		<div v-else class="content-container">
			<div class="documents-layout">
				<div class="table-wrapper">
					<table
						class="documents-table"
						:class="{ 'documents-table--with-assignment-columns': showAssignmentColumns }"
					>
						<thead>
							<tr class="table-header-row">
								<th class="col-title">Title</th>
								<th class="col-mode">Completion mode</th>
								<th v-if="showAssignmentColumns" class="col-reference">Reference</th>
								<th v-if="showAssignmentColumns" class="col-counterparty">Counterparty</th>
								<th v-if="showAssignmentColumns" class="col-site-address">Site address</th>
								<th v-if="showAssignmentColumns" class="col-amount">Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="!filteredDocuments.length">
								<td :colspan="tableColumnCount" class="empty-cell">
									{{ emptyStateMessage }}
								</td>
							</tr>
							<PortalDocumentRow
								v-for="doc in filteredDocuments"
								:key="doc.id"
								:ref="(el) => registerRowRef(doc.id, el)"
								:document="doc"
								:show-assignment-columns="showAssignmentColumns"
								@archive="handleArchiveRequest"
								@request-preview-url="handleRemotePreview"
								@translation-preview="handleTranslationPreview"
								@translation-regenerate="handleTranslationRegenerate"
								@move-category="handleMoveCategory"
								@open-document="handleOpenDocument"
								@autosave-error="handleRowAutosaveError"
								@completion-mode-change-request="handleCompletionModeChangeRequest"
								@menu-open="closeOtherMenus"
							/>
						</tbody>
					</table>
				</div>

				<p v-if="formError" class="error-message form-error">
					{{ formError }}
				</p>
			</div>
		</div>

		<div v-if="showNewDocumentModal" class="modal-overlay" @click="cancelNewDocument">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Upload document</h3>

				<div class="form-group">
					<label for="newDocTitle">Title</label>
					<input
						id="newDocTitle"
						ref="newDocTitleInputRef"
						v-model="newDocumentForm.title"
						type="text"
						class="input"
						placeholder="e.g. Self-Employed agreement"
					/>
				</div>

				<div class="form-group">
					<label for="newDocCategory">Category</label>
					<select
						id="newDocCategory"
						v-model="newDocumentForm.category"
						class="input category-select"
						required
					>
						<option
							v-for="category in documentCategoryOptions"
							:key="category.value"
							:value="category.value"
						>
							{{ category.label }}
						</option>
					</select>
				</div>

				<div class="form-group">
					<label for="newDocFile">File</label>
					<input
						id="newDocFile"
						ref="newDocFileInputRef"
						type="file"
						class="input file-input"
						accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"
						@change="handleNewDocumentFile"
					/>
				</div>

				<p v-if="newDocumentError" class="error-message">{{ newDocumentError }}</p>

				<div class="modal-actions">
					<button class="modal-btn cancel" @click="cancelNewDocument" :disabled="isCreatingDocument">
						Cancel
					</button>
					<button
						class="modal-btn primary"
						@click="confirmNewDocument"
						:disabled="!canSubmitNewDocument || isCreatingDocument"
					>
						{{ isCreatingDocument ? 'Creating…' : 'Create document' }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Archive document</h3>
				<p class="modal-message">
					Archive "{{ archiveTarget?.title || 'this document' }}"? It will disappear from the supplier app but stay available in admin history.
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" @click="cancelDelete">Cancel</button>
					<button
						class="modal-btn confirm"
						@click="confirmDelete"
						:disabled="isDeletingItem"
					>
						{{ isDeletingItem ? 'Archiving...' : 'Archive' }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="modeChangeTarget" class="modal-overlay" @click="cancelCompletionModeChange">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Change completion mode?</h3>
				<p class="modal-message">
					"{{ modeChangeTarget.document?.title || 'This document' }}" already has signing history.
					Changing from {{ completionModeLabel(modeChangeTarget.currentMode) }} to
					{{ completionModeLabel(modeChangeTarget.nextMode) }} will pause or resume the current
					signing flow instead of deleting signed copies.
				</p>
				<p class="modal-message">
					{{ signingSummaryText }}
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" @click="cancelCompletionModeChange" :disabled="modeChangeSaving">
						Cancel
					</button>
					<button
						class="modal-btn primary"
						@click="confirmCompletionModeChange"
						:disabled="modeChangeSaving"
					>
						{{ modeChangeSaving ? 'Saving...' : 'Change mode' }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="showSigningProfileModal" class="modal-overlay" @click="closeSigningProfileModal">
			<div class="modal-content profile-modal" @click.stop>
				<h3 class="modal-title">My signing profile</h3>
				<p class="modal-message">
					This profile is used when you sign bilateral supplier documents before they are sent to the supplier. A single standard signature style is applied automatically.
				</p>

				<div class="form-group">
					<label for="signingLegalName">Legal name</label>
					<input
						id="signingLegalName"
						v-model="signingProfileForm.legalName"
						type="text"
						class="input"
						placeholder="Your legal signing name"
					/>
				</div>

				<div class="form-group">
					<label for="signingPhone">Phone (E.164)</label>
					<input
						id="signingPhone"
						v-model="signingProfileForm.phoneE164"
						type="text"
						class="input"
						placeholder="+447911123456"
					/>
					<p class="input-hint">OTP codes for owner signing will be sent to this number.</p>
				</div>

				<label class="check">
					<input v-model="signingConsentAccepted" type="checkbox" />
					<span>I agree to use this electronic signature for supplier documents.</span>
				</label>

				<p v-if="signingProfileError" class="error-message">{{ signingProfileError }}</p>

				<div class="modal-actions">
					<button class="modal-btn cancel" @click="closeSigningProfileModal">Cancel</button>
					<button
						class="modal-btn primary"
						@click="saveSigningProfile"
						:disabled="store.signingProfileLoading"
					>
						{{ store.signingProfileLoading ? 'Saving...' : 'Save profile' }}
					</button>
				</div>
			</div>
		</div>

		<SupplierDocumentPreviewModal
			:show="previewState.show"
			:title="previewState.title"
			:filename="previewState.filename"
			:mime-type="previewState.mimeType"
			:url="previewState.url"
			:html="previewState.html"
			:loading="previewState.loading"
			:error="previewState.error"
			@close="closeSupplierPreview"
			@open-original="handlePreviewOpenOriginal"
			@download="handlePreviewDownload"
		/>
	</div>
</template>

<script setup>
import {
	computed,
	getCurrentInstance,
	nextTick,
	onMounted,
	onUnmounted,
	reactive,
	ref,
	watch,
} from 'vue';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import {
	DEFAULT_SUPPLIER_DOCUMENT_CATEGORY,
	DOCUMENT_TRANSLATION_LANGUAGES,
	SUPPLIER_DOCUMENT_CATEGORIES,
	normalizeSupplierDocumentCategory,
	useSupplierPortalAdminStore,
} from '@/stores/supplierPortalAdmin';
import { useMediaStore } from '@/stores/media';
import { useProjectsStore } from '@/stores/projects';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import PortalDocumentRow from '@/components/settings/PortalDocumentRow.vue';
import SupplierDocumentPreviewModal from '@/components/settings/SupplierDocumentPreviewModal.vue';
import {
	deriveAssignmentConfirmationFilename,
	generateAssignmentConfirmationHtml,
	getAssignmentConfirmationTableSummary,
	isAssignmentConfirmationDocument,
} from '@/utils/assignmentConfirmationDocument';
import { buildLegacyClientDocumentFooterText } from '@/utils/clientDocumentTheme';
import { convertImageToDataUrl } from '@/utils/imageUtils';
import {
	buildProposalFooterText,
	downloadProposalPdfFromHtml,
	formatGbpAmount,
} from '@/utils/proposalDocument';

const emit = defineEmits(['create-template', 'open-document']);

const store = useSupplierPortalAdminStore();
const mediaStore = useMediaStore();
const projectsStore = useProjectsStore();
const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;

const search = ref('');
const activeCategory = ref('all');
const showCreateMenu = ref(false);
const createMenuRef = ref(null);
const formError = ref('');
const showDeleteModal = ref(false);
const archiveTarget = ref(null);
const isDeletingItem = ref(false);
const modeChangeTarget = ref(null);
const modeChangeSaving = ref(false);
const showSigningProfileModal = ref(false);
const signingProfileError = ref('');
const signingConsentAccepted = ref(false);
const signingProfileForm = reactive({
	legalName: '',
	phoneE164: '',
});

const showNewDocumentModal = ref(false);
const newDocumentError = ref('');
const isCreatingDocument = ref(false);
const newDocumentForm = reactive({
	title: '',
	category: DEFAULT_SUPPLIER_DOCUMENT_CATEGORY,
	file: null,
	fileName: '',
});
const newDocTitleInputRef = ref(null);
const newDocFileInputRef = ref(null);

const previewState = reactive({
	show: false,
	title: '',
	filename: '',
	mimeType: '',
	url: '',
	html: '',
	loading: false,
	error: '',
});
const previewContext = ref(null);
let previewRequestToken = 0;

const rowRefs = new Map();
const documentCategoryOptions = SUPPLIER_DOCUMENT_CATEGORIES;
const categoryFilters = [
	{ value: 'all', label: 'All categories' },
	...SUPPLIER_DOCUMENT_CATEGORIES,
];

const COMPLETION_MODE_LABELS = {
	read_only: 'Read only',
	bilateral_signature: 'Bilateral signature',
};
const TRANSLATION_POLL_MS = 5000;
const ACTIVE_TRANSLATION_STATUSES = new Set(['pending', 'generating', 'stale']);
let translationPollId = null;
const isRefreshingTranslations = ref(false);

const registerRowRef = (id, el) => {
	const key = String(id);
	if (!el) {
		rowRefs.delete(key);
		return;
	}
	rowRefs.set(key, el);
	if (typeof el.setSaveHandler === 'function') {
		el.setSaveHandler((payload) => persistDocumentEdit(id, payload));
	}
};

const closeOtherMenus = ({ id } = {}) => {
	const targetKey = id !== undefined && id !== null ? String(id) : null;
	rowRefs.forEach((rowEl, key) => {
		if (!rowEl || key === targetKey) return;
		rowEl.closePopovers?.();
	});
};

const isLoading = computed(() => store.loading && store.documents.length === 0);
const error = computed(() => store.error);
const documents = computed(() => store.documents || []);

const categoryLabel = (value) => {
	const normalized = normalizeSupplierDocumentCategory(value);
	return SUPPLIER_DOCUMENT_CATEGORIES.find((category) => category.value === normalized)?.label || '';
};

const activeDocuments = computed(() => {
	const list = documents.value.filter((doc) => !doc?.isArchived);
	const toTime = (value) => {
		if (!value) return 0;
		const time = new Date(value).getTime();
		return Number.isFinite(time) ? time : 0;
	};
	return list
		.slice()
		.sort((a, b) => toTime(b?.createdAt) - toTime(a?.createdAt));
});

const matchesSearch = (doc, q) => {
	if (!q) return true;
	const title = String(doc?.title || '').toLowerCase();
	const desc = String(doc?.description || '').toLowerCase();
	const file = String(doc?.file?.name || '').toLowerCase();
	const category = categoryLabel(doc?.category).toLowerCase();
	const assignment = getAssignmentConfirmationTableSummary(doc);
	const assignmentSearchText = assignment
		? [
				assignment.reference,
				assignment.counterparty,
				assignment.siteAddress,
				assignment.amount === null ? '' : assignment.amount,
				assignment.amount === null ? '' : formatGbpAmount(assignment.amount),
			]
				.join(' ')
				.toLowerCase()
		: '';
	return (
		title.includes(q) ||
		desc.includes(q) ||
		file.includes(q) ||
		category.includes(q) ||
		assignmentSearchText.includes(q)
	);
};

const categoryDocuments = computed(() =>
	activeDocuments.value.filter(
		(doc) =>
			activeCategory.value === 'all' ||
			normalizeSupplierDocumentCategory(doc?.category) === activeCategory.value,
	),
);

const filteredDocuments = computed(() => {
	const q = String(search.value || '').trim().toLowerCase();
	return categoryDocuments.value.filter((doc) => matchesSearch(doc, q));
});

const showAssignmentColumns = computed(() =>
	categoryDocuments.value.some(isAssignmentConfirmationDocument),
);
const tableColumnCount = computed(() => (showAssignmentColumns.value ? 6 : 2));

const emptyStateMessage = computed(() => {
	if (search.value.trim()) return 'No documents match your search.';
	if (activeCategory.value === 'all') {
		return 'No documents yet. Click "New document" to create one.';
	}
	return `No documents in ${categoryLabel(activeCategory.value)} yet.`;
});

const translationRefreshTargets = computed(() =>
	activeDocuments.value
		.filter((doc) =>
			Boolean(doc?.file?.id) &&
			(Array.isArray(doc?.translations) ? doc.translations : []).some((translation) =>
				ACTIVE_TRANSLATION_STATUSES.has(String(translation?.status || 'pending')),
			),
		)
		.map((doc) => doc.id)
		.filter(Boolean),
);

const hasActiveTranslationJobs = computed(() => translationRefreshTargets.value.length > 0);

const refreshActiveTranslations = async () => {
	if (isRefreshingTranslations.value) return;
	const documentIds = translationRefreshTargets.value;
	if (!documentIds.length) return;
	isRefreshingTranslations.value = true;
	try {
		await Promise.allSettled(
			documentIds.map((documentId) =>
				store.fetchDocumentTranslations(documentId, { silent: true }),
			),
		);
	} finally {
		isRefreshingTranslations.value = false;
	}
};

const stopTranslationPolling = () => {
	if (!translationPollId || typeof window === 'undefined') return;
	window.clearInterval(translationPollId);
	translationPollId = null;
};

const startTranslationPolling = ({ immediate = false } = {}) => {
	if (typeof window === 'undefined') return;
	if (!translationPollId) {
		translationPollId = window.setInterval(() => {
			void refreshActiveTranslations();
		}, TRANSLATION_POLL_MS);
	}
	if (immediate) {
		void refreshActiveTranslations();
	}
};

watch(
	hasActiveTranslationJobs,
	(active) => {
		if (active) {
			startTranslationPolling({ immediate: true });
			return;
		}
		stopTranslationPolling();
	},
	{ immediate: true },
);

const completionModeLabel = (mode) =>
	COMPLETION_MODE_LABELS[String(mode || '')] || 'Read only';

const pluralize = (count, singular, plural = `${singular}s`) =>
	`${count} ${count === 1 ? singular : plural}`;

const signingSummaryText = computed(() => {
	const summary = modeChangeTarget.value?.document?.signingSummary || {};
	const parts = [];
	const pending = Number(summary.pendingExecutions ?? 0) || 0;
	const awaitingSupplier = Number(summary.awaitingSupplierExecutions ?? 0) || 0;
	const completed = Number(summary.completedExecutions ?? 0) || 0;
	const superseded = Number(summary.supersededExecutions ?? 0) || 0;
	const cancelled = Number(summary.cancelledExecutions ?? 0) || 0;
	const total = Number(summary.totalExecutions ?? 0) || 0;
	if (pending) parts.push(pluralize(pending, 'pending request'));
	if (awaitingSupplier) parts.push(pluralize(awaitingSupplier, 'awaiting supplier signature'));
	if (completed) parts.push(pluralize(completed, 'completed signed copy', 'completed signed copies'));
	if (superseded) parts.push(pluralize(superseded, 'superseded request'));
	if (cancelled) parts.push(pluralize(cancelled, 'cancelled request'));
	if (parts.length) return `Signing history: ${parts.join(', ')}.`;
	return `Signing history: ${pluralize(total, 'record')}.`;
});

const refresh = async () => {
	try {
		await Promise.allSettled([store.fetchDocuments(), store.fetchSigningProfile()]);
	} catch (err) {
		// store.error already set
	}
};

const persistDocumentEdit = async (documentId, payload) => {
	if (!documentId) return;
	formError.value = '';

	const file = payload?.file || null;
	let fileId = null;
	if (file) {
		if (
			payload.completionMode === 'bilateral_signature' &&
			!String(file?.type || '').toLowerCase().includes('pdf') &&
			!/\.pdf$/i.test(String(file?.name || ''))
		) {
			throw new Error('Bilateral signature requires a PDF document');
		}
		const uploaded = await mediaStore.uploadFile(file);
		fileId = uploaded?.id || null;
	}

	const updatePayload = {
		title: payload.title,
		description: payload.description,
		completionMode: payload.completionMode,
		requiresEffectiveDate: payload.requiresEffectiveDate,
	};
	if (fileId) {
		updatePayload.fileId = fileId;
	}

	try {
		await store.updateDocument(documentId, updatePayload);
	} catch (err) {
		formError.value =
			store.error || err?.message || 'Failed to save document changes';
		toast?.error?.('Failed to save document');
		logError?.(err, 'PortalDocuments::persistDocumentEdit');
		throw err;
	}
};

const handleCompletionModeChangeRequest = ({ document: doc, currentMode, nextMode } = {}) => {
	if (!doc?.id || !nextMode) return;
	modeChangeTarget.value = { document: doc, currentMode, nextMode };
};

const cancelCompletionModeChange = () => {
	if (modeChangeSaving.value) return;
	modeChangeTarget.value = null;
};

const confirmCompletionModeChange = async () => {
	const target = modeChangeTarget.value;
	const documentId = target?.document?.id;
	if (!documentId || !target?.nextMode) {
		modeChangeTarget.value = null;
		return;
	}

	modeChangeSaving.value = true;
	formError.value = '';
	try {
		const rowEl = rowRefs.get(String(documentId));
		if (rowEl?.applyCompletionMode) {
			rowEl.applyCompletionMode(target.nextMode);
			await nextTick();
			await rowEl.flushIfDirty?.();
		} else {
			const nextMode = String(target.nextMode || 'read_only');
			await persistDocumentEdit(documentId, {
				title: target.document?.title || '',
				description: target.document?.description || '',
				completionMode: nextMode,
				requiresEffectiveDate: nextMode === 'bilateral_signature',
				file: null,
			});
		}
		modeChangeTarget.value = null;
		toast?.success?.('Completion mode updated');
	} catch (err) {
		formError.value = store.error || err?.message || 'Failed to save document changes';
		toast?.error?.('Failed to save document');
		logError?.(err, 'PortalDocuments::confirmCompletionModeChange');
	} finally {
		modeChangeSaving.value = false;
	}
};

const handleRowAutosaveError = (err) => {
	formError.value =
		store.error || err?.message || 'Failed to save document changes';
};

// New document actions
const closeCreateMenu = () => {
	showCreateMenu.value = false;
};

const toggleCreateMenu = () => {
	showCreateMenu.value = !showCreateMenu.value;
};

const createAssignmentConfirmation = () => {
	closeCreateMenu();
	emit('create-template', {
		templateKey: 'assignment-confirmation-v1',
		category: 'assignments',
		sourceType: 'template',
	});
};

const openNewDocumentModal = () => {
	closeCreateMenu();
	newDocumentForm.title = '';
	newDocumentForm.category =
		activeCategory.value === 'all'
			? DEFAULT_SUPPLIER_DOCUMENT_CATEGORY
			: normalizeSupplierDocumentCategory(activeCategory.value);
	newDocumentForm.file = null;
	newDocumentForm.fileName = '';
	newDocumentError.value = '';
	if (newDocFileInputRef.value) newDocFileInputRef.value.value = '';
	showNewDocumentModal.value = true;
	nextTick(() => newDocTitleInputRef.value?.focus?.());
};

const cancelNewDocument = () => {
	if (isCreatingDocument.value) return;
	showNewDocumentModal.value = false;
};

const handleNewDocumentFile = (event) => {
	const [file] = event.target.files || [];
	if (!file) {
		newDocumentForm.file = null;
		newDocumentForm.fileName = '';
		return;
	}
	newDocumentForm.file = file;
	newDocumentForm.fileName = file.name || '';
};

const canSubmitNewDocument = computed(
	() =>
		Boolean(newDocumentForm.title.trim()) &&
		Boolean(newDocumentForm.file) &&
		Boolean(newDocumentForm.category),
);

const confirmNewDocument = async () => {
	if (!canSubmitNewDocument.value || isCreatingDocument.value) return;
	newDocumentError.value = '';
	isCreatingDocument.value = true;
	try {
		const uploaded = await mediaStore.uploadFile(newDocumentForm.file);
		const doc = await store.createDocument({
			title: newDocumentForm.title.trim(),
			description: '',
			category: normalizeSupplierDocumentCategory(newDocumentForm.category),
			sourceType: 'upload',
			completionMode: 'read_only',
			requiresEffectiveDate: false,
			fileId: uploaded?.id || null,
		});
		showNewDocumentModal.value = false;
		toast?.success?.('Document created');
		nextTick(() => {
			if (!doc?.id) return;
			const rowEl = rowRefs.get(String(doc.id));
			rowEl?.focusTitle?.();
		});
	} catch (err) {
		newDocumentError.value =
			store.error || err?.message || 'Failed to create document';
		toast?.error?.('Failed to create document');
		logError?.(err, 'PortalDocuments::confirmNewDocument');
	} finally {
		isCreatingDocument.value = false;
	}
};

const handleOpenDocument = ({ document: doc } = {}) => {
	if (!doc?.id) return;
	emit('open-document', { document: doc });
};

const handleMoveCategory = async ({ document: doc, category } = {}) => {
	if (!doc?.id || !category) return;
	const nextCategory = normalizeSupplierDocumentCategory(category);
	if (normalizeSupplierDocumentCategory(doc.category) === nextCategory) return;
	try {
		await store.updateDocument(doc.id, { category: nextCategory });
		toast?.success?.(`Moved to ${categoryLabel(nextCategory)}`);
	} catch (err) {
		toast?.error?.(store.error || 'Unable to move document');
		logError?.(err, 'PortalDocuments::moveCategory');
	}
};

// Archive
const handleArchiveRequest = ({ document: doc }) => {
	if (!doc?.id) return;
	archiveTarget.value = doc;
	showDeleteModal.value = true;
};

const cancelDelete = () => {
	if (isDeletingItem.value) return;
	showDeleteModal.value = false;
	archiveTarget.value = null;
};

const confirmDelete = async () => {
	const doc = archiveTarget.value;
	if (!doc?.id) {
		showDeleteModal.value = false;
		return;
	}
	isDeletingItem.value = true;
	try {
		await store.archiveDocument(doc.id);
		showDeleteModal.value = false;
		archiveTarget.value = null;
		toast?.success?.('Document archived');
	} catch (err) {
		toast?.error?.(store.error || 'Unable to archive');
		logError?.(err, 'PortalDocuments::confirmDelete');
	} finally {
		isDeletingItem.value = false;
	}
};

// Supplier document preview/open/download actions intentionally stay feature-local.
// Client document and invoice previews continue to use their existing flows.
const findTranslation = (doc, language) =>
	(Array.isArray(doc?.translations) ? doc.translations : []).find(
		(translation) => String(translation?.language) === String(language),
	) || null;

const translationLabel = (language) =>
	DOCUMENT_TRANSLATION_LANGUAGES.find((entry) => entry.language === language)?.label ||
	String(language || '').toUpperCase();

const resolvePreviewFilename = ({ document: doc, language = null } = {}) => {
	if (language) {
		return findTranslation(doc, language)?.file?.name || `${doc?.title || 'document'}-${language}`;
	}
	if (doc?.file?.name) return doc.file.name;
	if (isAssignmentConfirmationDocument(doc) && doc?.content) {
		return deriveAssignmentConfirmationFilename(doc);
	}
	return 'document';
};

const resolvePreviewFile = ({ document: doc, language = null } = {}) =>
	language ? findTranslation(doc, language)?.file || null : doc?.file || null;

const buildAssignmentPreviewContent = (content = {}) => {
	const project = projectsStore.activeProject || {};
	const settings = project.businessSettings || {};
	const storedCompany = content.company || {};
	const company = {
		name: settings.name || project.name || storedCompany.name || '',
		address: settings.address || storedCompany.address || '',
		companyNumber:
			settings.regNumber || settings.companyNumber || storedCompany.companyNumber || '',
		phone: settings.phone || storedCompany.phone || '',
		email: settings.email || storedCompany.email || '',
		website: settings.website || storedCompany.website || '',
	};
	const projectFooter = buildProposalFooterText(company);
	const generatedFooters = [
		buildProposalFooterText(storedCompany),
		buildLegacyClientDocumentFooterText(storedCompany),
		buildLegacyClientDocumentFooterText(company),
	];
	const footerText =
		!content.footerText || generatedFooters.includes(content.footerText)
			? projectFooter
			: content.footerText;

	return {
		...content,
		company,
		footerText,
	};
};

const resolveProjectLogoUrl = async () => {
	if (!projectsStore.initialized) {
		try {
			await projectsStore.ensureInitialized?.();
		} catch (err) {
			logError?.(err, 'PortalDocuments::assignmentPreviewProject');
		}
	}

	let resolvedLogoUrl = '';
	const logo = projectsStore.activeProject?.logo || null;
	if (logo?.id) {
		try {
			const signed = await mediaStore.getSignedUrl(logo.id);
			resolvedLogoUrl = signed?.url || '';
		} catch (err) {
			logError?.(err, 'PortalDocuments::assignmentPreviewLogo');
		}
	}
	if (!resolvedLogoUrl && logo?.url) {
		resolvedLogoUrl = logo.url;
	}
	resolvedLogoUrl = resolvedLogoUrl || '/logo-m.svg';
	if (resolvedLogoUrl.startsWith('/')) return resolvedLogoUrl;
	const dataUrl = await convertImageToDataUrl(resolvedLogoUrl);
	return dataUrl || resolvedLogoUrl;
};

const beginSupplierPreview = ({ document: doc, language = null } = {}) => {
	previewRequestToken += 1;
	const file = resolvePreviewFile({ document: doc, language });
	previewContext.value = { document: doc, language, file };
	Object.assign(previewState, {
		show: true,
		title: language
			? `${doc?.title || 'Supplier document'} — ${translationLabel(language)}`
			: doc?.title || 'Supplier document',
		filename: resolvePreviewFilename({ document: doc, language }),
		mimeType: file?.mime || (file?.name ? '' : 'application/pdf'),
		url: '',
		html: '',
		loading: Boolean(file?.id),
		error: '',
	});
	return previewRequestToken;
};

const closeSupplierPreview = () => {
	previewRequestToken += 1;
	previewState.show = false;
	previewState.loading = false;
	previewContext.value = null;
};

const resolveSourceSignedUrl = async ({ document: doc, language = null, disposition = 'view' }) => {
	if (language) {
		const translationFile = findTranslation(doc, language)?.file || null;
		if (disposition === 'download') {
			if (!translationFile?.id) return null;
			return mediaStore.getSignedUrl(translationFile.id, { disposition: 'download' });
		}
		return store.getDocumentTranslationSignedUrl({
			documentId: doc?.id,
			language,
		});
	}
	if (!doc?.file?.id) return null;
	return mediaStore.getSignedUrl(doc.file.id, { disposition });
};

const loadRemotePreview = async ({ document: doc, language = null, token }) => {
	try {
		const signed = await resolveSourceSignedUrl({
			document: doc,
			language,
			disposition: 'view',
		});
		if (token !== previewRequestToken || !previewState.show) return;
		const url = signed?.url || '';
		if (!url) {
			previewState.error = language
				? 'No translation preview is available.'
				: 'No preview is available for this document.';
			return;
		}
		previewState.url = url;
	} catch (err) {
		if (token !== previewRequestToken || !previewState.show) return;
		previewState.error = language
			? 'Unable to load the translation preview.'
			: 'Unable to load the document preview.';
		logError?.(err, language ? 'PortalDocuments::translationPreview' : 'PortalDocuments::preview');
	} finally {
		if (token === previewRequestToken) {
			previewState.loading = false;
		}
	}
};

const loadAssignmentPreview = async ({ document: doc, token }) => {
	previewState.loading = true;
	try {
		const logoUrl = await resolveProjectLogoUrl();
		if (token !== previewRequestToken || !previewState.show) return;
		previewState.html = generateAssignmentConfirmationHtml({
			content: buildAssignmentPreviewContent(doc.content),
			logoUrl,
		});
	} catch (err) {
		if (token !== previewRequestToken || !previewState.show) return;
		previewState.error = 'Unable to generate the assignment preview.';
		logError?.(err, 'PortalDocuments::templatePreview');
	} finally {
		if (token === previewRequestToken) {
			previewState.loading = false;
		}
	}
};

const handleRemotePreview = ({ document: doc } = {}) => {
	if (!doc) return;
	const token = beginSupplierPreview({ document: doc });
	if (doc?.file?.id) {
		void loadRemotePreview({ document: doc, token });
		return;
	}
	if (isAssignmentConfirmationDocument(doc) && doc?.content) {
		void loadAssignmentPreview({ document: doc, token });
		return;
	}
	previewState.loading = false;
	previewState.error = 'This document does not have a file or previewable template content.';
};

const handleTranslationPreview = ({ document: doc, language } = {}) => {
	if (!doc?.id || !language) return;
	const translation = findTranslation(doc, language);
	if (translation?.status !== 'ready' || !translation?.file?.id) return;
	const token = beginSupplierPreview({ document: doc, language });
	void loadRemotePreview({ document: doc, language, token });
};

const triggerBrowserDownload = ({ url, filename }) => {
	if (!url || typeof document === 'undefined') return;
	const link = document.createElement('a');
	link.href = url;
	link.download = String(filename || 'document').trim() || 'document';
	link.rel = 'noopener';
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const downloadSource = async ({ document: doc, language = null } = {}) => {
	if (!doc || (language && !doc.id)) return;
	try {
		const signed = await resolveSourceSignedUrl({
			document: doc,
			language,
			disposition: 'download',
		});
		const url = signed?.url || '';
		if (!url) {
			toast?.error?.('No download link is available');
			return;
		}
		triggerBrowserDownload({
			url,
			filename: resolvePreviewFilename({ document: doc, language }),
		});
	} catch (err) {
		toast?.error?.(store.error || 'Unable to download the file');
		logError?.(err, 'PortalDocuments::download');
	}
};

const handlePreviewOpenOriginal = async () => {
	const context = previewContext.value;
	if (!context?.document) return;

	const pendingTab = window.open('about:blank', '_blank');
	if (pendingTab) pendingTab.opener = null;
	try {
		const signed = await resolveSourceSignedUrl({
			...context,
			disposition: 'view',
		});
		const url = signed?.url || '';
		if (!url) {
			pendingTab?.close?.();
			toast?.error?.('No original file link is available');
			return;
		}
		if (pendingTab?.location?.replace) {
			pendingTab.location.replace(url);
			return;
		}
		window.open(url, '_blank', 'noopener');
	} catch (err) {
		pendingTab?.close?.();
		toast?.error?.(store.error || 'Unable to open the original file');
		logError?.(err, 'PortalDocuments::openOriginal');
	}
};

const handlePreviewDownload = async () => {
	if (previewState.html && !previewState.url) {
		try {
			await downloadProposalPdfFromHtml({
				html: previewState.html,
				filename: previewState.filename || 'assignment-confirmation.pdf',
				logError,
			});
		} catch (err) {
			toast?.error?.('Unable to download the generated PDF');
			logError?.(err, 'PortalDocuments::downloadGeneratedPreview');
		}
		return;
	}
	const context = previewContext.value;
	if (context?.document) {
		await downloadSource(context);
	}
};

const handleTranslationRegenerate = async ({ document: doc, language }) => {
	if (!doc?.id || !language) return;
	try {
		await store.regenerateDocumentTranslation({
			documentId: doc.id,
			language,
		});
		toast?.success?.('Translation queued');
		startTranslationPolling({ immediate: true });
	} catch (err) {
		toast?.error?.(store.error || 'Unable to regenerate translation');
		logError?.(err, 'PortalDocuments::translationRegenerate');
	}
};

// Signing profile modal
const syncSigningProfileForm = (profile = store.signingProfile) => {
	signingProfileForm.legalName = profile?.legalName || '';
	signingProfileForm.phoneE164 = profile?.phoneE164 || '';
	signingConsentAccepted.value = Boolean(profile?.eSignConsentAt);
	signingProfileError.value = '';
};

const openSigningProfileModal = async () => {
	signingProfileError.value = '';
	if (!store.signingProfile && !store.signingProfileLoading) {
		try {
			await store.fetchSigningProfile();
		} catch (err) {
			signingProfileError.value = store.error || 'Failed to load signing profile';
		}
	}
	syncSigningProfileForm();
	showSigningProfileModal.value = true;
};

const closeSigningProfileModal = () => {
	showSigningProfileModal.value = false;
	signingProfileError.value = '';
};

const saveSigningProfile = async () => {
	signingProfileError.value = '';
	try {
		await store.saveSigningProfile({
			legalName: signingProfileForm.legalName,
			phoneE164: signingProfileForm.phoneE164,
			consentAccepted: signingConsentAccepted.value,
		});
		syncSigningProfileForm();
		showSigningProfileModal.value = false;
		toast?.success?.('Signing profile updated');
	} catch (err) {
		signingProfileError.value =
			store.error || err?.message || 'Failed to save signing profile';
		logError?.(err, 'PortalDocuments::saveSigningProfile');
	}
};

const handleCreateMenuOutsideClick = (event) => {
	if (!showCreateMenu.value) return;
	if (createMenuRef.value && !createMenuRef.value.contains(event.target)) {
		closeCreateMenu();
	}
};

onMounted(() => {
	refresh();
	document.addEventListener('click', handleCreateMenuOutsideClick);
});

onUnmounted(() => {
	stopTranslationPolling();
	document.removeEventListener('click', handleCreateMenuOutsideClick);
});

// Documents added or changed on another device show up here without reloading. The list is read
// again quietly, and only replaced while no menu, dialog or upload is in progress.
const isPortalListBusy = () =>
	store.loading ||
	showCreateMenu.value ||
	showDeleteModal.value ||
	Boolean(archiveTarget.value || modeChangeTarget.value || previewContext.value) ||
	isDeletingItem.value ||
	modeChangeSaving.value ||
	showSigningProfileModal.value ||
	showNewDocumentModal.value ||
	isCreatingDocument.value ||
	isRefreshingTranslations.value;

const refreshPortalDocumentsQuietly = async ({ canApply }) => {
	if (isPortalListBusy()) return;
	const fresh = await store.peekDocuments();
	if (!canApply() || isPortalListBusy()) return;
	if (sameRecords(store.documents, fresh)) return;
	store.documents = fresh;
};

useLiveRefresh(refreshPortalDocumentsQuietly, { isBusy: isPortalListBusy });
</script>

<style scoped>
.portal-documents {
	min-width: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	min-height: 0;
	gap: 16px;
	box-sizing: border-box;
	color: var(--text-color);
}

.header-bar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
	min-height: 40px;
}

.section-title {
	margin: 0;
	color: var(--text-color);
	font: 600 1.5rem/2rem var(--font-family-display);
}

.input {
	border: 1px solid var(--border-color);
	border-radius: 4px;
	padding: 10px 12px;
	font-size: 0.9rem;
	background: var(--noc-background);
	color: var(--text-color);
	max-height: 40px;
	transition: background-color 0.15s ease, border-color 0.15s ease;
}

.content-container {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.documents-layout {
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-height: 0;
}

.loading-container,
.error-container {
	padding: 24px 16px;
	text-align: center;
	color: var(--text-color-secondary);
}

.loading-spinner {
	width: 32px;
	height: 32px;
	margin: 0 auto 12px;
	border-radius: 50%;
	border: 3px solid var(--border-color);
	border-top-color: var(--noc-blue);
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.error-message {
	color: var(--noc-red, #d9534f);
	margin: 0;
}

.form-error {
	padding: 8px 12px;
	background: var(--noc-red-10);
	border-radius: 4px;
	font-size: 0.85rem;
}

/* The square table frame also contains the existing narrow-screen row layout. */
.table-wrapper {
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 0;
	box-shadow: none;
	min-width: 0;
	overflow: visible;
	display: flex;
	flex-direction: column;
	position: relative;
	container-type: inline-size;
	container-name: documents-table;
}

.documents-table {
	width: 100%;
	max-width: 100%;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	font: 400 0.875rem/1.25rem var(--font-family-base);
}

.documents-table th,
.documents-table :deep(td) {
	box-sizing: border-box;
	padding: 8px 16px;
	border-bottom: 1px solid var(--border-color);
	text-align: left;
	vertical-align: middle;
}

.documents-table :deep(td) {
	height: 40px;
}

.documents-table tbody tr:last-child :deep(td) {
	border-bottom: none;
}

.documents-table th {
	height: 38px;
	background: #fafafa;
	font: 600 0.8125rem/1.125rem var(--font-family-base);
	color: var(--text-color);
	position: sticky;
	top: 0;
	z-index: 1;
}

@container documents-table (min-width: 921px) {
	.documents-table tbody tr:not(:last-child) :deep(td:first-child) {
		border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
	}
}

.col-title {
	width: 75%;
}

.col-mode {
	width: 25%;
	min-width: 0;
}

.documents-table--with-assignment-columns .col-title {
	width: 21%;
}

.documents-table--with-assignment-columns .col-reference {
	width: 13%;
}

.documents-table--with-assignment-columns .col-counterparty {
	width: 17%;
}

.documents-table--with-assignment-columns .col-site-address {
	width: 21%;
}

.documents-table--with-assignment-columns .col-amount {
	width: 14%;
	text-align: right;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

.documents-table--with-assignment-columns .col-mode {
	width: 14%;
	min-width: 0;
}

.documents-table--with-assignment-columns :deep(td.col-amount) {
	text-align: right;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

.documents-table--with-assignment-columns :deep(td.col-amount .assignment-cell-value) {
	width: auto;
	min-width: max-content;
	overflow: visible;
	text-overflow: clip;
}

.empty-cell {
	text-align: center;
	color: var(--text-color-secondary);
	padding: 32px;
}

@container documents-table (max-width: 920px) {
	.documents-table {
		table-layout: auto;
		display: block;
	}

	.documents-table thead {
		display: none;
	}

	.documents-table tbody {
		display: block;
	}

	.documents-table tbody tr {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		grid-template-areas: 'title mode';
		column-gap: 12px;
		row-gap: 10px;
		padding: 14px 16px;
		border-bottom: 1px solid var(--border-color);
		align-items: center;
	}

	.documents-table tbody tr.table-row--with-assignment-columns {
		grid-template-areas:
			'title mode'
			'reference counterparty'
			'site-address amount';
	}

	.documents-table tbody tr:last-child {
		border-bottom: none;
	}

	.documents-table :deep(td) {
		display: flex;
		align-items: center;
		border: none;
		padding: 0;
		height: auto;
		min-width: 0;
		width: auto;
	}

	.documents-table :deep(td:last-child) {
		padding-right: 0;
	}

	.documents-table :deep(td.col-reference),
	.documents-table :deep(td.col-counterparty),
	.documents-table :deep(td.col-site-address),
	.documents-table :deep(td.col-amount) {
		flex-direction: column;
		align-items: flex-start;
		text-align: left;
	}

	.documents-table :deep(.col-title) {
		grid-area: title;
		min-width: 0;
	}

	.documents-table :deep(.col-mode) {
		grid-area: mode;
	}

	.documents-table :deep(.col-reference) {
		grid-area: reference;
	}

	.documents-table :deep(.col-counterparty) {
		grid-area: counterparty;
	}

	.documents-table :deep(.col-site-address) {
		grid-area: site-address;
	}

	.documents-table :deep(.col-amount) {
		grid-area: amount;
	}

	.documents-table tbody tr:has(> .empty-cell) {
		display: block;
		padding: 0;
		border-bottom: none;
	}

	.empty-cell {
		display: block;
		padding: 24px;
	}
}

@container documents-table (max-width: 520px) {
	.documents-table tbody tr {
		grid-template-columns: 1fr;
		grid-template-areas:
			'title'
			'mode';
		column-gap: 10px;
		row-gap: 12px;
		padding: 14px;
	}

	.documents-table tbody tr.table-row--with-assignment-columns {
		grid-template-areas:
			'title'
			'mode'
			'reference'
			'counterparty'
			'site-address'
			'amount';
	}

	.documents-table :deep(.col-mode) {
		justify-self: start;
	}

}

/* Modals */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-content {
	background: var(--noc-background);
	border-radius: 8px;
	border: 1px solid var(--border-color);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	padding: 24px;
	min-width: 320px;
	max-width: 90%;
	width: min(520px, 92vw);
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.profile-modal {
	width: min(760px, 92vw);
	max-height: 88vh;
	overflow: auto;
}

.modal-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 600;
	color: var(--text-color);
}

.modal-message {
	margin: 0;
	font-size: 0.95rem;
	color: var(--text-color-secondary);
	line-height: 1.5;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.form-group label {
	font-size: 0.85rem;
	color: var(--text-color-secondary);
}

.input-hint {
	margin: 2px 0 0;
	font-size: 0.82rem;
	color: var(--text-color-secondary);
}

.file-input {
	max-height: none;
	height: 43px;
	padding: 0 15px;
	line-height: 43px;
	display: flex;
	align-items: center;
}

.category-select {
	max-height: none;
	height: 40px;
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

.check {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	color: var(--text-color);
	font-size: 0.9rem;
	cursor: pointer;
	padding: 10px 14px;
	border: 1px solid var(--border-color);
	border-radius: 4px;
	background: var(--noc-background);
}

.check input[type="checkbox"] {
	width: 15px;
	height: 15px;
	cursor: pointer;
	accent-color: var(--noc-blue);
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
}

.modal-btn {
	padding: 8px 20px;
	border-radius: 4px;
	border: none;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease, background 0.15s ease;
	font-size: 0.8rem;
}

.modal-btn.cancel {
	background: transparent;
	color: var(--text-color-secondary);
	border: 1px solid var(--border-color);
}

.modal-btn.cancel:hover {
	background: var(--noc-background-secondary);
	color: var(--text-color);
}

.modal-btn.confirm {
	background: var(--noc-red);
	color: #fff;
}

.modal-btn.confirm:hover:not(:disabled) {
	background: var(--noc-red-80);
}

.modal-btn.primary {
	background: var(--noc-blue);
	color: #fff;
}

.modal-btn.primary:hover:not(:disabled) {
	filter: brightness(1.06);
}

.modal-btn:disabled {
	opacity: 0.7;
	cursor: not-allowed;
}
</style>

<style scoped src="@/assets/styles/document-list-controls.css"></style>
